import { PropsWithChildren, useMemo } from 'react';
import { toast as sonnerToast } from 'sonner';

import { Icon, IconName } from '@/components/Icon';

import { mergeClassNames } from '@/utils';

import { useIsOnline } from '@/hooks/useIsOnline';

import styles from './Toast.module.css';

interface InnerToastProps extends PropsWithChildren {
  id: string | number;
  title?: string;
  description?: string;
  icon?: IconName;
  iconColor?: string;
  iconSpin?: boolean;
  duration?: number;
  dismissible?: boolean;
  button?: {
    label: string;
    onClick: (id: string | number) => void;
    icon?: IconName;
  };
}

export interface ToastProps extends Omit<InnerToastProps, 'id'> {
  variant?: 'success' | 'error' | 'loading';
}

export function toast({
  variant,
  icon,
  iconColor,
  iconSpin,
  button,
  description,
  dismissible,
  duration,
  title,
  ...props
}: ToastProps) {
  const iconForVariant = () => {
    switch (variant) {
      case 'success':
        return 'checkmark';
      case 'error':
        return 'error';
      case 'loading':
        return 'spinner';
      default:
        return undefined;
    }
  };

  const iconColorForVariant = () => {
    switch (variant) {
      case 'success':
        return 'var(--ion-color-success-tint)';
      case 'error':
        return 'var(--ion-color-danger-tint)';
      case 'loading':
        return 'var(--ion-color-success)';
      default:
        return 'rgba(255,255,255,0.4)';
    }
  };

  const durationForVariant = () => {
    switch (variant) {
      case 'error':
        return 7000;
      case 'loading':
        return -1;
      case 'success':
      default:
        return 5000;
    }
  };

  return sonnerToast.custom(
    (id) => (
      <Toast
        icon={icon ?? iconForVariant()}
        iconColor={iconColor ?? iconColorForVariant()}
        iconSpin={iconSpin ?? variant === 'loading'}
        {...(button && {
          label: button.label,
          onClick: () => button.onClick(id),
        })}
        button={button}
        description={description}
        duration={duration ?? durationForVariant()}
        id={id}
        title={title}
        {...props}
      />
    ),
    {
      duration,
      dismissible,
    },
  );
}

const Toast = ({ title, description, button, id, icon, iconColor, iconSpin, ...props }: InnerToastProps) => {
  const isOnline = useIsOnline();
  const newDescription = isOnline ? description : 'You are currently offline. Some features may not work as expected.';
  const newTitle = isOnline ? title : 'Offline Mode';
  return (
    <div className={styles.toast} {...props}>
      <div className={styles.content}>
        {icon && (
          <div className={mergeClassNames(styles.icon, iconSpin && styles.spin)}>
            <Icon color={iconColor ?? 'rgba(255,255,255,0.4)'} name={icon} size={24} />
          </div>
        )}
        {!props.children && (
          <div className={styles.textContent}>
            <p className={styles.title}>{newTitle}</p>
            <p className={styles.description}>{newDescription}</p>
          </div>
        )}
        {props.children}
      </div>
      {button?.label && (
        <div className={styles.action}>
          <button
            className={styles.button}
            onClick={() => {
              button.onClick(id);
              sonnerToast.dismiss(id);
            }}>
            {button.icon && <Icon name={button.icon} size={14} />}
            <span className={styles.buttonText}>{button.label}</span>
          </button>
        </div>
      )}
    </div>
  );
};
